"use client";
import { useMemo, useRef, useState } from "react";
import classNames from "classnames";

import delay from "lodash/delay";

import Button from "@stadium/shared/src/components/shared/button";
import TextInput from "@stadium/ui/src/components/form/TextInput";
import Label from "@stadium/ui/src/components/form/Label";
import {
  ResponsiveDrawerDropdown,
  DropdownToggle,
  DropdownItem,
} from "@stadium/ui/src/components/bootstrap-dropdown";
import {
  Accordion,
  AccordionToggle,
  AccordionCollapse,
} from "@stadium/ui/src/components/accordion/bootstrap-accordion/styled";
import CheckboxInput from "@stadium/ui/src/components/form/CheckboxInput";

import { OtherInputProps } from "@stadium/shared/src/components/book-a-call/form";

import {
  DEFAULT_ARR,
  DEMO_OFFERING_LABEL_COUNT,
  FORM_FIELDS_KEYS,
  getCustomizeYourDemoOfferings,
  OTHER_KEY,
} from "constants/book-a-call";

import defaultStyles from "./main.module.scss";

type CustomizeDemoDropdownProps = {
  formValues: any;
  errorFieldsRef: any;
  formErrors: any;
  handleFieldChange: any;
  styles?: ModularStyles;
};

const CustomizeDemoDropdown = ({
  formValues,
  errorFieldsRef,
  formErrors,
  handleFieldChange,
  styles = defaultStyles,
}: CustomizeDemoDropdownProps) => {
  const { offerings: offeringsError } = formErrors;
  const {
    offerings: { value: offeringsValue, otherValue: offeringsOtherValue },
  } = formValues;

  const [activeKeys, setActiveKeys] = useState(DEFAULT_ARR);
  const inputRef = useRef(null);
  const accordionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const CUSTOMIZE_YOUR_DEMO_OFFERINGS = getCustomizeYourDemoOfferings();

  const allOfferingsValue = useMemo(() => {
    return [...offeringsValue, offeringsOtherValue].filter(Boolean);
  }, [offeringsValue, offeringsOtherValue]);

  const allOfferingsValueLength = allOfferingsValue.length;

  const offeringLabelCount = DEMO_OFFERING_LABEL_COUNT;

  const renderCustomizeYourDemoLabel = ({ placeholderText }) => {
    if (allOfferingsValueLength === 0) {
      return (
        <span>
          <span className={styles.placeholderText}>{placeholderText}</span>
        </span>
      );
    }

    return (
      <span>
        {allOfferingsValueLength <= offeringLabelCount
          ? allOfferingsValue.join(", ")
          : `${allOfferingsValue.slice(0, offeringLabelCount).join(", ")} + ${allOfferingsValueLength - offeringLabelCount
          }`}
      </span>
    );
  };

  const focusOtherInput = () => {
    delay(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 300);
  };

  const focusAccordion = (key: string) => {
    delay(() => {
      const accordionElement = accordionRefs?.current?.[key];
      if (accordionElement) {
        try {
          accordionElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }
    }, 400);
  };

  const toggleSection = (key: string) => {
    const isExpanding = !activeKeys.includes(key);
    setActiveKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [key]));

    if (isExpanding) {
      focusAccordion(key);
    }

    if (key === OTHER_KEY) {
      focusOtherInput();
    }
  };

  const handleCustomizeYourDemoToggle = (isOpen) => {
    if (!isOpen) {
      const selectedGroupKeys = CUSTOMIZE_YOUR_DEMO_OFFERINGS.filter((group) => {
        if (group.key === OTHER_KEY) {
          return !!offeringsOtherValue;
        }
        return group.options.some((item) => offeringsValue.includes(item.title));
      }).map((group) => group.key);
      setActiveKeys(selectedGroupKeys);
    }
  };

  const handleOfferingChange = ({ value }) => {
    handleFieldChange({
      name: FORM_FIELDS_KEYS.offerings,
      value: offeringsValue.includes(value)
        ? offeringsValue.filter((offering) => offering !== value)
        : [...offeringsValue, value],
      otherValue: offeringsOtherValue,
    });
  };

  const renderOtherTextInputOrTextArea = ({
    fieldName,
    otherPlaceHolderText = "Please specify",
  }: OtherInputProps) => {
    return (
      <TextInput
        name={fieldName}
        id={fieldName}
        value={offeringsOtherValue}
        ref={inputRef}
        type="text"
        classes={`${styles.commonOtherInput} ${styles.demoOtherInput} ${styles.commonOtherInputWithBorder}`}
        className={styles.bookCallFormInput}
        inputContainerClass={styles.otherInputContainer}
        placeholder={otherPlaceHolderText}
        onChange={({ value }) =>
          handleFieldChange({
            name: FORM_FIELDS_KEYS.offerings,
            value: offeringsValue,
            otherValue: value,
          })
        }
      />
    );
  };

  const renderCheckboxDropdownOptions = ({ options, key }) => (
    <div className={styles.listingPanel}>
      {options.map(({ key: optionKey, title: optionTitle, subTitle: optionSubTitle }) => {
        const isChecked = offeringsValue.includes(optionTitle);
        return (
          <CheckboxInput
            key={`${key}-${optionKey}`}
            name={FORM_FIELDS_KEYS.offerings}
            id={`${key}-${optionKey}`}
            error={!!offeringsError}
            classes={classNames(styles.demoRowCheckoutOptionV2, {
              [styles.active]: isChecked,
            })}
            value={optionTitle}
            onChange={() => handleOfferingChange({ value: optionTitle })}
            checked={isChecked}
            label={
              <Label styles={styles} iconType="check" className={styles.customizeYourDemoRowLabel}>
                <div>
                  <div>{optionTitle}</div>
                  {optionSubTitle && (
                    <div className={styles.dropdownItemSubTitle}>{optionSubTitle}</div>
                  )}
                </div>
              </Label>
            }
          />
        );
      })}
    </div>
  );

  return (
    <div
      className={`${styles.bookCallFormFieldHolder} ${styles.customizeYourDemoRowWithApplyBtn} ${styles.customizeYourDemoSmallDropdown}`}
      ref={errorFieldsRef.offerings}
    >
      <div className={styles.label}>Customize Your Demo</div>
      <div className={styles.bookCallFormResponsiveDropdown}>
        <ResponsiveDrawerDropdown
          key={FORM_FIELDS_KEYS.offerings}
          styles={styles}
          menuClassName={classNames(
            styles.dropDownOptions,
            styles.dropDownOptionsV2,
            styles.dropDownOptionsFixedPosition,
          )}
          label={
            <DropdownToggle
              title="Customize Your Demo"
              styles={styles}
              truncate
              className={classNames({ [styles.errorBorder]: !!offeringsError })}
            >
              {renderCustomizeYourDemoLabel({
                placeholderText: "I’d like to learn more about...",
              })}
            </DropdownToggle>
          }
          onToggle={handleCustomizeYourDemoToggle}
          heading={
            <DropdownToggle className={styles.demoDropDownHeading}>
              Customize Your Demo
            </DropdownToggle>
          }
        >
          {CUSTOMIZE_YOUR_DEMO_OFFERINGS.map(({ key, title, options }) => (
            <Accordion
              key={`${key}-${title}`}
              eventKey={key}
              activeKey={activeKeys.includes(key) ? key : ""}
              className={styles.demoOfferingAccordion}
            >
              <AccordionToggle
                eventKey={key}
                className={styles.panelLabelToggle}
                onClick={() => toggleSection(key)}
                showArrow={key !== OTHER_KEY}
              >
                <div
                  ref={(el) => (accordionRefs.current[key] = el)}
                  className={classNames(styles.panelLabel)}
                >
                  {title}
                </div>
              </AccordionToggle>
              <AccordionCollapse eventKey={key}>
                {key === OTHER_KEY
                  ? renderOtherTextInputOrTextArea({
                    title: "Other",
                    fieldName: FORM_FIELDS_KEYS.offerings,
                    otherPlaceHolderText: "Please specify",
                  })
                  : renderCheckboxDropdownOptions({ options, key })}
              </AccordionCollapse>
            </Accordion>
          ))}

          <div className={`${styles.demoApplyCtaContainer} ${styles.demoApplyCtaContainerV2}`}>
            <DropdownItem className="">
              <Button
                disabled={allOfferingsValueLength === 0}
                onClick={(e) => e.preventDefault()}
                title="Apply"
                styleType="primary"
                className={styles.btnApply}
              >
                Apply
              </Button>
            </DropdownItem>
          </div>
        </ResponsiveDrawerDropdown>
      </div>
    </div>
  );
};

export default CustomizeDemoDropdown;
